import { Component, Input, OnInit, ViewChild } from '@angular/core'
import { Store } from '@ngrx/store'
import {
  BdcContentV2Service,
  BdcPhotoMetadata,
  BdcPendingPhoto,
  BdcUnitResponse,
  BdcListingPhotoStatus,
} from '@tokeet-frontend/channel-content'
import {
  ActionFailed,
  ConfirmDialogService,
  Toaster,
  CloudinaryUploadDialogService,
  TokeetImagePayload,
} from '@tokeet-frontend/tv3-platform'
import { filter, finalize, map, switchMap } from 'rxjs/operators'
import { combineLatest, forkJoin } from 'rxjs'
import { SelectItemDialogService } from '@tv3/shared/dialogs/select-item-dialog/select-item-dialog.service'
import { PhotoGridComponent } from '../shared/photo-grid/photo-grid.component'
import { BDCPhotoGridCardItem, transformToBDCPhotoGridCardItem } from '../shared/photo-card/photo-card.interface'
import { FormControl } from '@angular/forms'

interface BdcUnitWithName extends BdcUnitResponse {
  name: string
}

@Component({
  selector: 'app-bdc-property-photos',
  templateUrl: './bdc-property-photos.component.html',
  styleUrls: ['./bdc-property-photos.component.scss'],
})
export class BdcPropertyPhotosComponent implements OnInit {
  @ViewChild('photoGrid') photoGrid: PhotoGridComponent

  @Input() propertyId: number
  @Input() roomId?: number
  @Input() isFraud?: boolean

  photos: BDCPhotoGridCardItem[] = []
  filteredPhotos: BDCPhotoGridCardItem[] = []
  pendingPhotos: BDCPhotoGridCardItem[] = []
  isLoading = true
  units: BdcUnitWithName[] = []
  get selectedPhotos(): Set<string> {
    return this.photoGrid?.selectedPhotos
  }

  unitCtrl = new FormControl()

  constructor(
    private store: Store<any>,
    private toaster: Toaster,
    private confirm: ConfirmDialogService,
    private contentService: BdcContentV2Service,
    private cloudinaryUploadDialog: CloudinaryUploadDialogService,
    private selectItemDialog: SelectItemDialogService
  ) {}

  ngOnInit(): void {
    this.loadUnits()
    this.onRefresh()
    this.unitCtrl.valueChanges.subscribe(() => this.filterPhotos())
  }

  loadUnits(): void {
    combineLatest([
      this.contentService.getUnitMetadata(),
      this.contentService.getPropertyUnits(this.propertyId),
    ]).subscribe(
      ([metadata, units]) => {
        this.units = units.map((unit) => ({
          ...unit,
          name: metadata.unit_names.find((m) => m.unit_name_id === unit.unit_name_id)?.unit_name || 'Unknown',
        }))
      },
      (error) => {
        this.store.dispatch(ActionFailed({ error }))
      }
    )
  }

  filterPhotos(): void {
    this.filteredPhotos = this.unitCtrl.value
      ? this.photos.filter((p) => p.rooms.includes(this.unitCtrl.value))
      : this.photos
  }

  getUnitPhotosCount(unitId: number, photos: BDCPhotoGridCardItem[]) {
    return photos.filter((p) => p.rooms.includes(unitId))?.length || 0
  }

  onRefresh(): void {
    const photos$ = this.contentService.getPropertyPhotos(this.propertyId)
    const pendingPhotos$ = this.contentService
      .getPropertyPendingPhotos(this.propertyId)
      .pipe(map((items) => items.filter((t) => t.status !== BdcListingPhotoStatus.OK)))

    forkJoin({
      photos: photos$,
      pendingPhotos: pendingPhotos$,
    })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        ({ photos, pendingPhotos }) => {
          this.photos = photos?.map((t) => transformToBDCPhotoGridCardItem(t)) || []
          this.pendingPhotos = pendingPhotos.map((t) => transformToBDCPhotoGridCardItem(t)) || []
          this.selectedPhotos?.clear()
          this.filterPhotos()
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  onAddPhotos(): void {
    this.cloudinaryUploadDialog
      .open({
        title: $localize`Upload Property Photos`,
      })
      .pipe(
        switchMap((images: TokeetImagePayload[]) => {
          return this.contentService.uploadPropertyPhotos(
            this.propertyId,
            images.map((image) => ({ url: image.url, tag_ids: [] }))
          )
        })
      )
      .subscribe(
        (pendingPhotos: BdcPendingPhoto[]) => {
          this.toaster.success($localize`Photos uploaded successfully`)
          this.onRefresh()
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  onDeletePhoto(photoId: string): void {
    this.confirm
      .confirm({
        title: $localize`Delete Photo`,
        body: $localize`Are you sure you want to delete this photo?`,
      })
      .pipe(switchMap(() => this.contentService.deletePropertyPhoto(this.propertyId, photoId)))
      .subscribe(
        () => {
          this.toaster.success($localize`Photo deleted successfully`)
          this.onRefresh()
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  onBatchDelete(): void {
    if (this.selectedPhotos.size === 0) return

    this.confirm
      .confirm({
        title: $localize`Delete Selected Photos`,
        body: $localize`Are you sure you want to delete ${this.selectedPhotos.size} selected photos?`,
      })
      .pipe(
        switchMap(() => {
          const deleteOperations = Array.from(this.selectedPhotos).map((photoId) =>
            this.contentService.deletePropertyPhoto(this.propertyId, photoId)
          )
          return forkJoin(deleteOperations)
        })
      )
      .subscribe(
        () => {
          this.toaster.success($localize`Selected photos deleted successfully`)
          this.onRefresh()
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  isPhotoSelected(photo: BdcPhotoMetadata): boolean {
    return this.selectedPhotos.has(photo.photo_id)
  }

  onAddToRoomUnitGallery(): void {
    if (this.selectedPhotos.size === 0) {
      return
    }

    this.selectItemDialog
      .open({
        title: $localize`Select Room`,
        help: $localize`Please select a room to add the photos to`,
        list: this.units,
        bindLabel: 'name',
        bindValue: 'unit_id',
        placeholder: $localize`Select Room`,
      })
      .pipe(
        filter((selectedUnitId) => !!selectedUnitId),
        switchMap((selectedUnitId) =>
          this.contentService.updateRoomPhotoGallery(this.propertyId, selectedUnitId, {
            insert_photos: Array.from(this.selectedPhotos),
          })
        )
      )
      .subscribe(
        () => {
          this.toaster.success($localize`Photos added to room gallery successfully`)
          this.selectedPhotos.clear()
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  onAddToPropertyGallery(): void {
    if (this.selectedPhotos.size === 0) {
      return
    }

    this.contentService
      .updatePropertyPhotoGallery(this.propertyId, {
        insert_photos: Array.from(this.selectedPhotos),
      })
      .subscribe(
        () => {
          this.toaster.success($localize`Photos added to property gallery successfully`)
          this.selectedPhotos.clear()
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }
}
